import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { EmailAccount } from "@/lib/types"

export function useEmailAccounts() {
  return useQuery({
    queryKey: ["email_accounts"],
    queryFn: () => api.get<{ data: EmailAccount[] }>("/api/v1/email_accounts"),
  })
}

export function useEmailAccount(id: string | undefined) {
  return useQuery({
    queryKey: ["email_accounts", id],
    queryFn: () => api.get<EmailAccount>(`/api/v1/email_accounts/${id}`),
    enabled: !!id,
  })
}

export function useCreateEmailAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<EmailAccount>("/api/v1/email_accounts", { email_account: data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email_accounts"] })
    },
  })
}

export function useUpdateEmailAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<EmailAccount>(`/api/v1/email_accounts/${id}`, { email_account: data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email_accounts"] })
    },
  })
}

export function useDeleteEmailAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/email_accounts/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email_accounts"] })
    },
  })
}

export function useTestEmailConnection() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ status: string; message: string }>(`/api/v1/email_accounts/${id}/test_connection`),
  })
}
